import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Loader2, AlertCircle } from 'lucide-react';
import { apiService, LLMParameterDefinition } from '../services/api';
import { Alert, AlertDescription } from './ui/alert';

interface ResolvedParameters {
  parameters: Record<string, LLMParameterDefinition>;
  inheritedFromProvider: string[];
  modelOverrides: string[];
  unsupported: string[];
}

interface ParameterPreviewProps {
  modelId: string;
  title?: string;
  description?: string;
}

export function ParameterPreview({
  modelId,
  title = 'Berechnete Parameter',
  description = 'Finale Parameter nach Anwendung von Vererbung und Überschreibungen',
}: ParameterPreviewProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resolved, setResolved] = useState<ResolvedParameters | null>(null);

  useEffect(() => {
    loadParameters();
  }, [modelId]);

  const loadParameters = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getModelParameters(modelId);
      setResolved(response);
    } catch (err: any) {
      setError(err.message || 'Failed to load parameters');
    } finally {
      setLoading(false);
    }
  };

  const getParamSource = (paramName: string): 'provider' | 'model' | 'unsupported' => {
    if (resolved?.unsupported.includes(paramName)) return 'unsupported';
    if (resolved?.modelOverrides.includes(paramName)) return 'model';
    if (resolved?.inheritedFromProvider.includes(paramName)) return 'provider';
    return 'provider';
  };

  const getSourceBadge = (source: 'provider' | 'model' | 'unsupported') => {
    switch (source) {
      case 'provider':
        return <Badge variant="outline" className="text-blue-700 border-blue-200">Provider</Badge>;
      case 'model':
        return <Badge variant="outline" className="text-green-700 border-green-200">Modell</Badge>;
      case 'unsupported':
        return <Badge variant="outline" className="text-red-700 border-red-200">Nicht unterstützt</Badge>;
    }
  };

  const formatValue = (value: any): string => {
    if (value === null || value === undefined) return 'null';
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    return String(value);
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          <span>Lade Parameter...</span>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (!resolved) {
    return null;
  }

  const paramEntries = Object.entries(resolved.parameters);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
        <div className="flex gap-2 pt-2">
          <div className="text-sm text-gray-600">
            <span className="font-medium">{paramEntries.length}</span> Parameter
          </div>
          <div className="text-sm text-gray-600">
            • <span className="font-medium">{resolved.inheritedFromProvider.length}</span> vom Provider
          </div>
          <div className="text-sm text-gray-600">
            • <span className="font-medium">{resolved.modelOverrides.length}</span> Überschreibungen
          </div>
          <div className="text-sm text-gray-600">
            • <span className="font-medium">{resolved.unsupported.length}</span> Nicht unterstützt
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {paramEntries.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            Keine Parameter konfiguriert
          </div>
        ) : (
          <div className="space-y-3">
            {paramEntries.map(([name, param]) => {
              const source = getParamSource(name);
              return (
                <div key={name} className="border border-gray-200 rounded-lg p-3">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm font-semibold">{name}</span>
                      {getSourceBadge(source)}
                      <Badge variant="secondary" className="text-xs">{param.type}</Badge>
                      {param.nullable && <Badge variant="outline" className="text-xs">nullable</Badge>}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                    {param.default !== null && param.default !== undefined && (
                      <div>
                        <span className="font-medium">Default:</span> {formatValue(param.default)}
                      </div>
                    )}
                    {param.min !== null && param.min !== undefined && (
                      <div>
                        <span className="font-medium">Min:</span> {param.min}
                      </div>
                    )}
                    {param.max !== null && param.max !== undefined && (
                      <div>
                        <span className="font-medium">Max:</span> {param.max}
                      </div>
                    )}
                  </div>
                  {param.description && (
                    <div className="text-sm text-gray-500 mt-1">{param.description}</div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {resolved.unsupported.length > 0 && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="text-sm font-medium text-red-800 mb-1">
              Nicht unterstützte Parameter:
            </div>
            <div className="flex flex-wrap gap-1">
              {resolved.unsupported.map((param) => (
                <Badge key={param} variant="outline" className="text-red-700 border-red-300">
                  {param}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
